import { ColorInput, NumberInput, SegmentedControl, Select, Stack, Textarea } from "@mantine/core";

import type { TextLayer } from "./model";
import {
  MAX_TEXT_LENGTH,
  MAX_TEXT_SIZE,
  MIN_TEXT_SIZE,
  TEXT_FONT_IDS,
  TEXT_FONTS,
  hasWeight,
  supportedWeight,
  type TextValues,
} from "./text";

const FONT_OPTIONS = TEXT_FONT_IDS.map((id) => ({ value: id, label: TEXT_FONTS[id].family }));
const ALIGN_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];

function isAlign(value: string): value is TextLayer["align"] {
  return ALIGN_OPTIONS.some((option) => option.value === value);
}

export interface TextControlsProps {
  layer: TextLayer;
  /** While a save runs, as live's. */
  locked: boolean;
  /** The values shown: the layer's, or the ones last asked for while the layer catches up. */
  values: TextValues;
  /** New values for the layer: it is measured again and kept centred where it was. */
  onChange: (layer: TextLayer, values: TextValues) => void;
}

/**
 * Live's text controls (spec G13b; `text-controls`): "Text", "Font" of live's ten, "Regular" or
 * "Bold" (Bold off where the font has none), "Size" 8 to 1,024, "Color" applied when picked, and
 * "Left", "Center" or "Right". Each change is one undo step, as live's.
 */
export function TextControls({ layer, locked, values, onChange }: TextControlsProps) {
  // A change builds on the values shown, so one made while the last is measured keeps it.
  const change = (patch: Partial<TextValues>) => onChange(layer, { ...values, ...patch });
  return (
    <Stack gap="xs">
      <Textarea
        disabled={locked}
        label="Text"
        size="xs"
        autosize
        minRows={2}
        maxRows={5}
        maxLength={MAX_TEXT_LENGTH}
        value={values.text}
        onChange={(event) => change({ text: event.currentTarget.value })}
      />
      <Select
        disabled={locked}
        label="Font"
        size="xs"
        data={FONT_OPTIONS}
        allowDeselect={false}
        value={values.font}
        onChange={(font) => {
          if (font !== null) change({ font, weight: supportedWeight(font, values.weight) });
        }}
      />
      <SegmentedControl
        disabled={locked}
        size="xs"
        fullWidth
        aria-label="Weight"
        data={[
          { value: "regular", label: "Regular" },
          { value: "bold", label: "Bold", disabled: !hasWeight(values.font, "bold") },
        ]}
        value={values.weight}
        onChange={(weight) => change({ weight: weight === "bold" ? "bold" : "regular" })}
      />
      <NumberInput
        disabled={locked}
        label="Size"
        size="xs"
        min={MIN_TEXT_SIZE}
        max={MAX_TEXT_SIZE}
        allowDecimal={false}
        value={values.size}
        onChange={(value) => {
          const size = Number(value);
          // Live applies a size only within its bounds.
          if (Number.isInteger(size) && size >= MIN_TEXT_SIZE && size <= MAX_TEXT_SIZE) change({ size });
        }}
      />
      <ColorInput disabled={locked} label="Color" size="xs" format="hex" value={values.color} onChangeEnd={(color) => change({ color })} />
      <SegmentedControl
        disabled={locked}
        size="xs"
        fullWidth
        aria-label="Alignment"
        data={ALIGN_OPTIONS}
        value={values.align}
        onChange={(align) => {
          if (isAlign(align)) change({ align });
        }}
      />
    </Stack>
  );
}
